import React from "react";
import { Alert, Box, IconButton, Tab, Tabs, TextField, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CustomButton from "components/CustomButton";
import { useSnackbar } from "hooks/useSnackbar";
import { decryptSecretValue, encryptSecretValue } from "services/miscService";
import { SECRET_AUTO_HIDE_MS } from "utils/secrets";

const EncryptionUtilityPanel = () => {
  const { showSnackbar } = useSnackbar();
  const [tab, setTab] = React.useState(0);
  const [input, setInput] = React.useState("");
  const [output, setOutput] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (tab !== 1 || !output) return undefined;
    const timer = setTimeout(() => setOutput(""), SECRET_AUTO_HIDE_MS);
    return () => clearTimeout(timer);
  }, [tab, output]);

  const changeTab = (_: unknown, next: number) => {
    if (loading) return;
    setTab(next);
    setInput("");
    setOutput("");
  };

  const run = async () => {
    try {
      setLoading(true);
      const resp = tab === 0 ? await encryptSecretValue(input) : await decryptSecretValue(input.trim());
      setOutput(resp.data.value);
    } catch (error) {
      setOutput("");
      showSnackbar(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const copy = () =>
    navigator.clipboard.writeText(output).then(
      () => showSnackbar("Copied to clipboard", "success"),
      () => showSnackbar("Failed to copy", "error"),
    );

  return (
    <Box>
      <Tabs value={tab} onChange={changeTab}>
        <Tab label="Encrypt" disabled={loading} />
        <Tab label="Decrypt" disabled={loading} />
      </Tabs>
      <Alert severity="info" sx={{ my: 2 }}>
        {tab === 0
          ? "Encrypt a secret, then paste the enc: result into Extra Data or a configuration field. Every use is audited."
          : "Paste an enc: value to see its plaintext. The result clears after 30 seconds. Every use is audited."}
      </Alert>
      <TextField
        fullWidth
        multiline
        minRows={3}
        label={tab === 0 ? "Value to encrypt" : "Encrypted value (enc:...)"}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        autoComplete="off"
      />
      <Box sx={{ mt: 2 }}>
        <CustomButton
          label={loading ? "Working..." : tab === 0 ? "Encrypt" : "Decrypt"}
          onClick={run}
          disabled={loading || !input.trim()}
        />
      </Box>
      {output && (
        <Box sx={{ mt: 3, display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Typography sx={{ fontFamily: "monospace", wordBreak: "break-all", flex: 1 }}>{output}</Typography>
          <IconButton onClick={copy} size="small">
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default EncryptionUtilityPanel;
